// src/features/invite/InviteUsers.jsx
import { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Button, 
  Table, 
  Alert,
  Modal,
  Spinner
} from 'react-bootstrap';
import { 
  Users, 
  Mail, 
  Send, 
  UserPlus, 
  Clock, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Search,
  Trash2,
  Calendar,
  Shield,
  UserCheck,
  UserX,
  Link2,
  Lock
} from 'lucide-react';
import { businessService } from '../../services';
import { useAuth } from '../../context';
import { PERMISSIONS } from '../../constants';
import './InviteUsers.css';

export default function InviteUsers() {
  const { hasPermission } = useAuth();

  // ✅ Permission checks
  const canCreate = hasPermission(PERMISSIONS.USERS.CREATE);
  const canDelete = hasPermission(PERMISSIONS.USERS.DELETE);
  const canView = hasPermission(PERMISSIONS.USERS.VIEW);

  // ✅ Show Actions column only if user can create or delete
  const canTakeAction = canCreate || canDelete;

  const [email, setEmail] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteSuccess, setShowInviteSuccess] = useState(false);
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState('');
  const [showResendSuccess, setShowResendSuccess] = useState(false);
  const [resendSuccessMessage, setResendSuccessMessage] = useState('');
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    expired: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Load roles and invites on mount
  useEffect(() => {
    loadRoles();
    loadInvites();
  }, []);

  const loadRoles = async () => {
    try {
      const result = await businessService.getRoles();
      if (result.success) {
        const roleHierarchy = ['club_viewer', 'club_staff', 'club_admin', 'company_admin', 'super_admin'];
        const sortedRoles = [...result.data].sort((a, b) => {
          const indexA = roleHierarchy.indexOf(a.name);
          const indexB = roleHierarchy.indexOf(b.name);
          return indexA - indexB;
        });
        setRoles(sortedRoles);
        setSelectedRoleId('');
      } else {
        setError('Failed to load roles. Please refresh the page.');
      }
    } catch (err) {
      if (err.status === 403) {
        setError('You do not have permission to view roles. Contact your administrator.');
      } else {
        setError('Could not load user roles. Please refresh the page.');
      }
    }
  };

  const loadInvites = async () => {
  setLoading(true);
  setError('');
  try {
    const result = await businessService.getInvites();
    if (result.success) {
      setInvites(result.data);
      updateStats(result.data);
    } else {
      if (result.status === 403) {
        setError('Access denied. You do not have permission to view invitations.');
      } else {
        setError(result.message || 'Failed to load invites. Please refresh the page.');
      }
    }
  } catch (err) {
    if (err.status === 403) {
      setError('Access denied. You do not have permission to view invitations.');
    } else {
      setError(err.message || 'Could not load invites. Please refresh the page.');
    }
  }
  setLoading(false);
};

  const updateStats = (data) => {
    const pending = data.filter(inv => inv.status === 'pending').length;
    const accepted = data.filter(inv => inv.status === 'accepted').length;
    const expired = data.filter(inv => inv.status === 'expired').length;
    setStats({
      total: data.length,
      pending,
      accepted,
      expired
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canCreate) return;

    if (!email) {
      setError('Please enter an email address');
      setRoleError('');
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (!selectedRoleId) {
      setRoleError('Please select a role');
      setError('');
      setTimeout(() => setRoleError(''), 5000);
      return;
    }

    setIsSubmitting(true);
    setError('');
    setRoleError('');

    try {
      const result = await businessService.sendInvite(email, selectedRoleId);
      if (result.success) {
        setInviteSuccessMessage(`✅ Invitation sent to ${email}`);
        setShowInviteSuccess(true);
        setEmail('');
        setSelectedRoleId('');
        await loadInvites();
        setTimeout(() => setShowInviteSuccess(false), 5000);
      } else {
        if (result.status === 403) {
          setError('You do not have permission to send invites. Contact your administrator.');
        } else {
          setError(result.message || 'Failed to send invitation');
        }
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      if (err.status === 403) {
        setError('You do not have permission to send invites. Contact your administrator.');
      } else {
        setError(err.message || 'Failed to send invitation. Please try again.');
      }
      setTimeout(() => setError(''), 5000);
    }
    setIsSubmitting(false);
  };

  const handleResend = async () => {
    if (!canCreate) return;
    if (!selectedInvite) return;
    
    setIsSubmitting(true);
    try {
      const result = await businessService.resendInvite(selectedInvite.uuid);
      if (result.success) {
        setResendSuccessMessage(`✅ Invitation resent to ${selectedInvite.email}`);
        setShowResendSuccess(true);
        setShowResendModal(false);
        await loadInvites();
        setTimeout(() => setShowResendSuccess(false), 5000);
      } else {
        if (result.status === 403) {
          setError('You do not have permission to resend invites. Contact your administrator.');
        } else {
          setError(result.message || 'Failed to resend invitation');
        }
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      if (err.status === 403) {
        setError('You do not have permission to resend invites. Contact your administrator.');
      } else {
        setError('Failed to resend invitation. Please try again.');
      }
      setTimeout(() => setError(''), 5000);
    }
    setIsSubmitting(false);
  };

  const confirmDelete = (invite) => {
    if (!canDelete) return;
    setDeleteTarget(invite);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!deleteTarget) return;
    
    setIsSubmitting(true);
    try {
      const result = await businessService.deleteInvite(deleteTarget.uuid);
      if (result.success) {
        setDeleteSuccessMessage(`✅ Invitation for ${deleteTarget.email} deleted successfully`);
        setShowDeleteSuccess(true);
        setShowDeleteModal(false);
        setDeleteTarget(null);
        await loadInvites();
        setTimeout(() => setShowDeleteSuccess(false), 5000);
      } else {
        if (result.status === 403) {
          setError('You do not have permission to delete invites. Contact your administrator.');
        } else {
          setError(result.message || 'Failed to delete invitation');
        }
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      if (err.status === 403) {
        setError('You do not have permission to delete invites. Contact your administrator.');
      } else {
        setError('Failed to delete invitation. Please try again.');
      }
      setTimeout(() => setError(''), 5000);
    }
    setIsSubmitting(false);
    setShowDeleteModal(false);
  };

  const getStatusStyle = (status) => {
    const styles = {
      pending: { background: '#FEF3C7', color: '#78350F' },
      accepted: { background: '#D1FAE5', color: '#064E3B' },
      expired: { background: '#FEE2E2', color: '#991B1B' }
    };
    return styles[status] || styles.pending;
  };

  const getRoleBadge = (roleName) => {
    const styles = {
      super_admin: { background: '#EDE9FE', color: '#4C1D95' },
      club_admin: { background: '#EDE9FE', color: '#4C1D95' },
      company_admin: { background: '#EDE9FE', color: '#4C1D95' },
      club_staff: { background: '#DBEAFE', color: '#1E3A8A' },
      club_viewer: { background: '#E0F2FE', color: '#0369A1' }
    };
    return styles[roleName] || styles.club_viewer;
  };

  const filteredInvites = invites.filter(invite => {
    const matchesFilter = filter === 'all' || invite.status === filter;
    const matchesSearch = invite.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (invite.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="invite-page">
      <div className="invite-container">
        {/* Header */}
        <div className="invite-header">
          <div className="invite-header-left">
            <div className="invite-header-icon">
              <Users size={24} color="white" />
            </div>
            <div>
              <h1 className="invite-title">Manage Invites</h1>
              <p className="invite-subtitle">Invite new members to join your golf community</p>
            </div>
          </div>
          <Button onClick={loadInvites} className="refresh-button">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert className="error-alert" onClose={() => setError('')} dismissible>
            <XCircle size={20} />
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="stats-grid">
          {[
            { label: 'Total Invites', value: stats.total, icon: Users, color: '#0ea5e9', bg: '#E0F2FE' },
            { label: 'Pending', value: stats.pending, icon: Clock, color: '#F59E0B', bg: '#FEF3C7' },
            { label: 'Accepted', value: stats.accepted, icon: UserCheck, color: '#10B981', bg: '#D1FAE5' },
            { label: 'Expired', value: stats.expired, icon: UserX, color: '#EF4444', bg: '#FEE2E2' }
          ].map((stat, index) => (
            <div 
              key={index}
              className="stat-card"
              onClick={() => {
                const filterMap = ['all', 'pending', 'accepted', 'expired'];
                setFilter(filterMap[index]);
              }}
              style={{ cursor: 'pointer' }}
            >
              <div>
                <div className="stat-label">{stat.label}</div>
                <div className="stat-value">{stat.value}</div>
              </div>
              <div className="stat-icon" style={{ background: stat.bg, color: stat.color }}>
                <stat.icon size={20} />
              </div>
            </div>
          ))}
        </div>

        {/* Main Card */}
        <Card className="main-card">
          <Card.Body className="main-card-body">
            {/* ✅ Invite Form - Only if user canCreate */}
            {canCreate ? (
              <div className="invite-form-container">
                <div className="invite-form-header">
                  <UserPlus size={20} color="#0ea5e9" />
                  <h3 className="invite-form-title">Send New Invitation</h3>
                </div>
                
                <Form onSubmit={handleSubmit}>
                  <div className="invite-form-row">
                    <div className="invite-form-email">
                      <Form.Control
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="invite-input"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="invite-form-role">
                      <div className="role-select-wrapper">
                        <Form.Select
                          value={selectedRoleId}
                          onChange={(e) => {
                            setSelectedRoleId(e.target.value);
                            setRoleError('');
                          }}
                          className={`invite-select ${roleError ? 'invite-select-error' : ''}`}
                          disabled={isSubmitting || roles.length === 0}
                        >
                          <option value="">Select a role</option>
                          {roles.map((role) => (
                            <option key={role.uuid} value={role.uuid}>
                              {role.name.replace('_', ' ').toUpperCase()}
                            </option>
                          ))}
                        </Form.Select>
                        <span className="required-star">*</span>
                      </div>
                      {roleError && (
                        <div className="role-error-message">
                          <XCircle size={14} />
                          {roleError}
                        </div>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="invite-submit-button"
                    >
                      {isSubmitting ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <Send size={18} />
                          Send Invite
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {showInviteSuccess && (
                    <div className="invite-success-message">
                      <CheckCircle size={18} />
                      {inviteSuccessMessage}
                    </div>
                  )}
                </Form>
              </div>
            ) : (
              /* ✅ View-only notice */
              <div className="invite-access-notice">
                <Lock size={20} />
                <div>
                  <strong>View only</strong>
                  <p>You don't have permission to send invitations. Contact your administrator for access.</p>
                </div>
              </div>
            )}

            {/* Resend Success Message */}
            {showResendSuccess && (
              <div className="invite-success-message" style={{ marginBottom: '16px' }}>
                <CheckCircle size={18} />
                {resendSuccessMessage}
              </div>
            )}

            {/* Delete Success Message */}
            {showDeleteSuccess && (
              <div className="invite-success-message" style={{ marginBottom: '16px', color: '#991B1B', background: '#FEF2F2', borderColor: '#FEE2E2' }}>
                <CheckCircle size={18} />
                {deleteSuccessMessage}
              </div>
            )}

            {/* Search and Filter */}
            <div className="search-filter-container">
              <div className="search-filter-left">
                <div className="search-wrapper">
                  <Search size={18} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search invites..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="filter-buttons">
                  {['all', 'pending', 'accepted', 'expired'].map((f) => (
                    <Button
                      key={f}
                      size="sm"
                      onClick={() => setFilter(f)}
                      className={`filter-button ${filter === f ? 'active' : ''}`}
                    >
                      {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="invite-count">
                <Users size={14} />
                {filteredInvites.length} invite{filteredInvites.length !== 1 ? 's' : ''} found
              </div>
            </div>

            {/* Table */}
            {loading && filteredInvites.length === 0 ? (
              <div className="loading-state">
                <Spinner animation="border" className="loading-spinner" />
                <p>Loading invites...</p>
              </div>
            ) : filteredInvites.length === 0 ? (
              <div className="empty-state">
                <Mail size={48} className="empty-icon" />
                <p className="empty-title">No invites found</p>
                <p className="empty-subtitle">
                  {filter === 'all' 
                    ? 'Start inviting users to your golf community' 
                    : `No ${filter} invites at the moment`}
                </p>
              </div>
            ) : (
              <div className="table-wrapper">
                <Table hover className="invite-table">
                  <thead>
                    <tr className="table-header">
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Invited</th>
                      {/* ✅ Show Actions column only if user can take action */}
                      {canTakeAction && (
                        <th className="actions-header">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvites.map((invite) => {
                      const statusStyle = getStatusStyle(invite.status);
                      const roleStyle = getRoleBadge(invite.role);
                      return (
                        <tr key={invite.uuid} className="table-row">
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar">
                                {invite.full_name?.[0] || invite.email[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="user-name">
                                  {invite.full_name || '—'}
                                </div>
                                <div className="user-email">
                                  <Mail size={12} />
                                  {invite.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '4px',
                              fontSize: '13px',
                              fontWeight: '600',
                              background: roleStyle.background,
                              color: roleStyle.color
                            }}>
                              {invite.role?.replace('_', ' ') || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '4px',
                              fontSize: '13px',
                              fontWeight: '600',
                              background: statusStyle.background,
                              color: statusStyle.color
                            }}>
                              {invite.status}
                            </span>
                          </td>
                          <td>
                            <div className="invite-date">
                              <Calendar size={12} />
                              {invite.invited_at ? new Date(invite.invited_at).toLocaleDateString() : '—'}
                            </div>
                          </td>
                          {/* ✅ Actions cell - only if canTakeAction */}
                          {canTakeAction && (
                            <td className="actions-cell">
                              <div className="actions-wrapper">
                                {invite.status === 'pending' && (
                                  <>
                                    {/* ✅ Resend - only if canCreate */}
                                    {canCreate && (
                                      <Button
                                        size="sm"
                                        className="action-button-resend"
                                        onClick={() => {
                                          setSelectedInvite(invite);
                                          setShowResendModal(true);
                                        }}
                                        disabled={isSubmitting}
                                        title="Resend invite"
                                      >
                                        <RefreshCw size={14} />
                                      </Button>
                                    )}
                                    {/* ✅ Delete - only if canDelete */}
                                    {canDelete && (
                                      <Button
                                        size="sm"
                                        className="action-button-delete"
                                        onClick={() => confirmDelete(invite)}
                                        disabled={isSubmitting}
                                        title="Delete invite"
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    )}
                                  </>
                                )}
                                {invite.status === 'accepted' && (
                                  <span style={{
                                    padding: '4px 12px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    background: '#D1FAE5',
                                    color: '#064E3B'
                                  }}>
                                    Joined
                                  </span>
                                )}
                                {invite.status === 'expired' && canCreate && (
                                  <Button
                                    size="sm"
                                    className="action-button-resend"
                                    onClick={() => {
                                      setSelectedInvite(invite);
                                      setShowResendModal(true);
                                    }}
                                    disabled={isSubmitting}
                                    title="Resend invite"
                                  >
                                    <RefreshCw size={14} />
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Footer */}
        <div className="invite-footer">
          <div className="footer-left">
            <Link2 size={14} />
            Retail One • {new Date().getFullYear()}
          </div>
          <div className="footer-right">
            <span>
              <Shield size={14} />
              Secure invites
            </span>
          </div>
        </div>

        {/* Resend Modal */}
        <Modal 
          show={showResendModal} 
          onHide={() => setShowResendModal(false)}
          centered
          className="resend-modal"
        >
          <Modal.Header className="modal-header">
            <Modal.Title className="modal-title">
              <Mail size={24} color="#0ea5e9" />
              Resend Invitation
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="modal-body">
            <p className="modal-text">Resend invitation to:</p>
            <p className="modal-email">{selectedInvite?.email}</p>
            <p className="modal-hint">A new invite link will be generated and sent.</p>
          </Modal.Body>
          <Modal.Footer className="modal-footer">
            <Button 
              variant="secondary" 
              onClick={() => setShowResendModal(false)}
              className="modal-cancel-button"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleResend}
              disabled={isSubmitting}
              className="modal-resend-button"
            >
              {isSubmitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Send size={16} />
                  Resend Invite
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal 
          show={showDeleteModal} 
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete the invitation for <strong>{deleteTarget?.email}</strong>?</p>
            <p style={{ fontSize: '13px', color: '#64748b' }}>This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Delete Invite'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}